import { PICTO_IDS, resolvePicto } from './art.js';
import { PictoImg, usePictoSet } from './PictoImg.js';
import { DARK_LINE_PICTO_IDS } from './pictoDarkLine.js';
import styles from './Pictogram.module.css';

// Pictogrammen (ONTWERP.md §9: icoon-eerst; docs/DESIGN.md §7.2). Volgorde van keuze:
//  1. zonder `color`: het pictogram uit de set van het thema (PictoImg: kleurrijk als <img> uit
//     assets/picto, rustig als lijn uit assets/picto-clean; nooit in de bundle);
//  2. met `color` of zonder kleurpictogram: de kleine inline lijnset hieronder (monochroom);
//  3. anders een zacht vlak met de eerste letter van het label.

const PATHS: Record<string, string> = {
  calendar:
    'M7 3v3M17 3v3M4 8h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
  task: 'M5 12l4 4L19 6M4 20h16',
  school: 'M3 9l9-4 9 4-9 4-9-4zM7 11v5c0 1.5 2.5 3 5 3s5-1.5 5-3v-5M21 9v6',
  sport: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18',
  music: 'M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM9 18V5l12-2v13',
  food: 'M6 3v7a3 3 0 0 0 6 0V3M9 3v18M17 3c-2 0-3 3-3 7h3v11',
  home: 'M3 11l9-8 9 8M5 10v10h14V10M10 20v-6h4v6',
  car: 'M5 16l1.5-6h11L19 16M3 16h18v3H3zM7 19v2M17 19v2M7 16h.01M17 16h.01',
  star: 'M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9L12 3z',
  bed: 'M3 18V8M3 12h18v6M3 16h18M6 12V9h5v3',
  bath: 'M4 12h16v3a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-3zM6 12V6a2 2 0 0 1 4 0M7 19v2M17 19v2',
  toys: 'M4 8h16v8H4zM8 8V6h8v2M9 12h.01M15 12h.01',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6',
  pets: 'M8 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM16 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM12 20c-3 0-6-2-6-5s2-5 6-5 6 2 6 5-3 5-6 5z',
  book: 'M4 4h6a2 2 0 0 1 2 2v14a2 2 0 0 0-2-2H4zM20 4h-6a2 2 0 0 0-2 2v14a2 2 0 0 1 2-2h6z',
  clean: 'M14 3l-4 8M8 11h8l2 10H6zM6 21h12',
  doctor: 'M12 5v14M5 12h14',
  party: 'M4 20l4-12 8 8-12 4zM14 4l1 2M19 6l-2 1M18 12l2 1',
  shop: 'M4 8h16l-1.5 11h-13zM9 8V6a3 3 0 0 1 6 0v2',
  family:
    'M8 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM16 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM3 20v-4a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v4M13 13h4a3 3 0 0 1 3 3v4',
  cloud: 'M7 18h10a4 4 0 0 0 0-8 6 6 0 0 0-11.5 2A3 3 0 0 0 7 18z',
  check: 'M5 12l4 4L19 6',
};

/** Alle id's die een beeld opleveren: kleurpictogrammen + de inline lijnset. */
export const PICTOGRAM_IDS: ReadonlyArray<string> = PICTO_IDS.concat(
  Object.keys(PATHS).filter((id) => PICTO_IDS.indexOf(id) < 0),
);

export interface PictogramProps {
  /** Icoon-id (bijv. 'soccer'); onbekend of leeg → letter-fallback uit `label`. */
  icon?: string | null;
  /** Tekst voor lezers/screenreaders én de letter-fallback. */
  label: string;
  /** Zijde in px (kind-primair 80/48, overig 40). */
  size?: number;
  /** Monochrome kleur; forceert de inline lijnversie als die bestaat. */
  color?: string;
  /** Decoratief (tekst staat ernaast): geen eigen naam voor schermlezers. */
  decorative?: boolean;
}

export function Pictogram({ icon, label, size = 48, color, decorative }: PictogramProps) {
  const set = usePictoSet();
  const a11y = decorative
    ? { 'aria-hidden': 'true' as const }
    : { role: 'img' as const, 'aria-label': label };
  const inline = icon ? PATHS[icon] : undefined;
  const picto = color && inline ? null : resolvePicto(icon);
  if (picto) {
    // Alleen kleurpictogrammen met donkere lijnen krijgen in het donker een licht plaatje; de eigen
    // kleurset leest op beide achtergronden, de rustige set volgt de tekstkleur.
    const darkLine = set === 'color' && DARK_LINE_PICTO_IDS.indexOf(picto) >= 0;
    return (
      <PictoImg
        id={picto}
        size={size}
        set={set}
        class={darkLine ? `${styles.image} ${styles.darkLine}` : styles.image}
        // Plaatje in avond/nacht: 8 % van de eigen maat (een %-padding zou de ouder volgen).
        style={darkLine ? `--picto-pad:${Math.round(size * 0.08)}px` : undefined}
        alt={decorative ? '' : label}
      />
    );
  }
  const style = `width:${size}px;height:${size}px;font-size:${Math.round(size * 0.5)}px;${
    color ? `color:${color}` : ''
  }`;
  if (!inline) {
    const letter = label.trim().charAt(0).toUpperCase() || '?';
    return (
      <span class={`${styles.pictogram} ${styles.letter}`} style={style} {...a11y}>
        {letter}
      </span>
    );
  }
  return (
    <span class={styles.pictogram} style={style} {...a11y}>
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d={inline} />
      </svg>
    </span>
  );
}
